import React, { ReactNode } from 'react';

import { BreadcrumbDropdownItem } from './BreadcrumbDropdown';

// Section paths the breadcrumb section-dropdown should never show — these
// are top-level routesConfig entries that are actions (logout) or aren't
// meaningful navigation targets from a crumb.
const HIDDEN_SECTION_PATHS = new Set(['/', '/logout', '/ai-chat']);

interface RouteNode {
  name?: string;
  path?: string;
  icon?: ReactNode;
  hideInMenu?: boolean;
  routes?: RouteNode[];
}

interface BuildOpts {
  /** Callback fired when a dropdown item is selected. Wired to navigate(). */
  onNavigate: (path: string) => void;
}

/**
 * Build dropdown items for the top-level section crumb (e.g. "Loans" in
 * `Loans > [Co] > Active Loans`). Walks the runtime routesConfig — which
 * already has the user's company id baked into sub-paths — and returns one
 * item per visible section. The icon for each section is rendered inline
 * with the label so the dropdown reads like the sidebar nav.
 */
export const buildSectionDropdownItems = (
  routesConfig: { routes?: RouteNode[] } | null | undefined,
  opts: BuildOpts
): BreadcrumbDropdownItem[] => {
  if (!routesConfig?.routes) return [];

  return routesConfig.routes
    .filter((r): r is RouteNode => Boolean(r))
    .filter(r => r.path && !r.hideInMenu && !HIDDEN_SECTION_PATHS.has(r.path))
    .map(r => ({
      key: r.path as string,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {r.icon ? (
            <span style={{ display: 'inline-flex', color: '#233867' }}>{r.icon}</span>
          ) : null}
          <span>{r.name}</span>
        </span>
      ),
      onClick: () => opts.onNavigate(r.path as string),
    }));
};

/**
 * Build dropdown items for sub-page crumbs within the currently active
 * section (e.g. when on `/loans/14/active-loans`, list every other page
 * under `/loans/14/...`). Returns the section's nested `routes` directly —
 * runtime routesConfig has already substituted the company id so each
 * item's path is fully-qualified and can be passed straight to navigate().
 */
export const buildSubPageDropdownItems = (
  routesConfig: { routes?: RouteNode[] } | null | undefined,
  sectionPath: string,
  opts: BuildOpts
): BreadcrumbDropdownItem[] => {
  if (!routesConfig?.routes) return [];

  const section = routesConfig.routes.find(
    (r): r is RouteNode => Boolean(r) && r.path === sectionPath
  );
  if (!section?.routes) return [];

  const flattened: RouteNode[] = [];
  const visit = (nodes: RouteNode[]) => {
    for (const node of nodes) {
      if (!node) continue;
      if (node.hideInMenu) {
        // hideInMenu is used for path-pattern wrappers (e.g. /dealers/:id)
        // — drill in but don't surface the wrapper itself.
        if (node.routes) visit(node.routes);
        continue;
      }
      if (node.path) flattened.push(node);
      if (node.routes) visit(node.routes);
    }
  };
  visit(section.routes);

  // De-dupe by path — some route trees contain duplicates after flattening.
  const seen = new Set<string>();
  return flattened
    .filter(r => {
      if (!r.path || seen.has(r.path)) return false;
      seen.add(r.path);
      return true;
    })
    .map(r => ({
      key: r.path as string,
      label: r.name ?? r.path,
      onClick: () => opts.onNavigate(r.path as string),
    }));
};
